import { useLayoutEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';

const retargetAttribute = (element, attribute, prefix, localIds) => {
    const value = element.getAttribute(attribute);
    if (!value) {
        return;
    }

    const next = value
        .split(/\s+/)
        .map(token => {
            if (token.startsWith(prefix) || !localIds.has(token)) {
                return token;
            }

            return `${prefix}${token}`;
        })
        .join(' ');

    if (next !== value) {
        element.setAttribute(attribute, next);
    }
};

export const UniqueFieldIds = ({ prefix, children }) => {
    const rootRef = useRef(null);

    useLayoutEffect(() => {
        const root = rootRef.current;
        if (!root) {
            return undefined;
        }

        const apply = () => {
            const elementsWithId = [...root.querySelectorAll('[id]')];
            const localIds = new Set(
                elementsWithId.filter(element => element.id).map(element => (element.id.startsWith(prefix) ? element.id.slice(prefix.length) : element.id)),
            );

            elementsWithId.forEach(element => {
                if (element.id && !element.id.startsWith(prefix)) {
                    element.id = `${prefix}${element.id}`;
                }
            });

            root.querySelectorAll('[for], [aria-labelledby], [aria-describedby]').forEach(element => {
                retargetAttribute(element, 'for', prefix, localIds);
                retargetAttribute(element, 'aria-labelledby', prefix, localIds);
                retargetAttribute(element, 'aria-describedby', prefix, localIds);
            });
        };

        apply();
        const observer = new MutationObserver(apply);
        observer.observe(root, {
            subtree: true,
            childList: true,
            attributes: true,
            attributeFilter: ['id', 'for', 'aria-labelledby', 'aria-describedby'],
        });

        return () => observer.disconnect();
    }, [prefix]);

    return (
        <Box ref={rootRef} sx={{ display: 'contents' }}>
            {children}
        </Box>
    );
};

UniqueFieldIds.propTypes = {
    prefix: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
};
